import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  ArrowsLeftRightIcon,
  EnvelopeIcon,
  UserIcon,
  UploadSimpleIcon,
  QuestionIcon,
} from '@phosphor-icons/react';
import { SalesOrderSource } from '@erp/shared-types';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';

interface Props {
  source: SalesOrderSource;
  size?: 'small' | 'medium';
  onClick?: () => void;
  sx?: React.CSSProperties;
}

const SourceBadge: React.FC<Props> = ({
  source,
  size = 'medium',
  onClick,
  sx,
}) => {
  // Map source values to more user-friendly display names
  const sourceDisplayMap: Record<string, string> = {
    edi: 'EDI',
    email: 'Email',
    direct_entry: 'Direct Entry',
    file_upload: 'File Upload',
  };

  // Map source values to color themes
  const sourceColorMap: Record<
    string,
    {
      bg: string;
      color: string;
      hoverBg: string;
    }
  > = {
    edi: {
      bg: 'rgba(156, 39, 176, 0.08)',
      color: 'secondary.main',
      hoverBg: 'rgba(156, 39, 176, 0.16)',
    },
    email: {
      bg: 'rgba(188, 170, 164, 0.08)',
      color: '#8d6e63',
      hoverBg: 'rgba(188, 170, 164, 0.16)',
    },
    direct_entry: {
      bg: 'rgba(255, 152, 0, 0.08)',
      color: 'warning.main',
      hoverBg: 'rgba(255, 152, 0, 0.16)',
    },
    file_upload: {
      bg: 'rgba(0, 188, 212, 0.08)',
      color: 'info.main',
      hoverBg: 'rgba(0, 188, 212, 0.16)',
    },
  };

  // Map source values to appropriate icons
  const sourceIconMap: Record<string, PhosphorIcon> = {
    edi: ArrowsLeftRightIcon,
    email: EnvelopeIcon,
    direct_entry: UserIcon,
    file_upload: UploadSimpleIcon,
  };

  const fontSize = size === 'small' ? 10 : 12;
  const iconSize = size === 'small' ? 12 : 14;
  const padding = size === 'small' ? '2px 6px' : '4px 10px';

  if (!source) {
    return <MDTypography variant="caption">-</MDTypography>;
  }

  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={0.5}
      onClick={onClick}
      sx={{
        fontSize,
        backgroundColor: sourceColorMap[source]?.bg || 'rgba(97, 97, 97, 0.08)',
        color: sourceColorMap[source]?.color || 'text.secondary',
        borderRadius: '16px',
        padding,
        width: 'fit-content',
        transition: 'all 0.2s ease-in-out',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor:
            sourceColorMap[source]?.hoverBg || 'rgba(97, 97, 97, 0.16)',
          transform: onClick ? 'translateY(-1px)' : 'none',
          boxShadow: onClick ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
        },
        ...sx,
      }}
    >
      {(() => {
        const SourceIcon = sourceIconMap[source] ?? QuestionIcon;
        return <SourceIcon size={iconSize} />;
      })()}
      <MDTypography
        color="inherit"
        sx={{
          fontSize,
          fontWeight: 'medium',
        }}
      >
        {sourceDisplayMap[source] || source}
      </MDTypography>
    </MDBox>
  );
};

export default SourceBadge;
